import { useState } from "react";
import Divider from '@mui/material/Divider';
import Backdrop from '@mui/material/Backdrop';
import TextField from '@mui/material/TextField';
import LinearProgressWithLabel from '../components/linear-progress-with-label';
import TextMetadataElement from '../components/text-metadata-element';
import ImageMetadataElement from '../components/image-metadata-element';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import Grid from '@mui/material/Grid2';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilePicker from '../components/file-picker'
import LoadingMetadataTable from '../components/loading-metadata-table'
import { useAuth } from 'oidc-react';
import { useDialogs } from '@toolpad/core/useDialogs';
import { useNotifications } from '@toolpad/core/useNotifications';
import Constants from '../config/constants';
import { hexToDecimal } from '../utils/color-util';
import { dataToChunks, digest, qrcode, generateShortCode } from '../utils/util';
import { uploadMissingFiles, handleZipDownload, handleCsvDownload, resolveZipName, resolveCsvName } from '../utils/file-util';
import { MagazinePageService } from '../services/magazine-page-service';
import { FileService } from '../services/file-service';


const NAME_TEMPLATE = 'CFA_Video_Slide_{name}_{code}.jpg';

const COLUMNS = [
	{
		width: 50,
		label: 'Image',
		dataKey: 'image',
		type: "IMAGE",
	},
	{
		width: 50,
		label: 'Artist Name',
		dataKey: 'artist',
		type: "TEXT",
	},
	{
		width: 50,
		label: 'Country',
		dataKey: 'country',
		type: "TEXT",
	},
	{
		width: 50,
		label: 'Description',
		dataKey: 'description',
		type: "TEXT",
	},
	{
		width: 50,
		label: 'Website',
		dataKey: 'website',
		type: "TEXT",
	},
];

const ARTIST_TEXT_ELEMENT_DEFAULTS = {
	xOffset: -1070,
	yOffset: -600,
	font: Constants.DEFAULT_FONT,
	color: { hex: Constants.DEFAULT_FONT_COLOR },
	fontSize: 70,
	style: Constants.STYLE_VERTICAL_ALIGN_CENTER | Constants.STYLE_ALIGN_CENTER,
	transform: (element) => {
		element.artist = element.artist ? element.artist.replace(/\s(.*)/, '{br/}{b}$1{/b}') : element.artist;
	},
};

const COUNTRY_TEXT_ELEMENT_DEFAULTS = {
	xOffset: -1070,
	yOffset: -330,
	font: Constants.DEFAULT_FONT,
	color: { hex: Constants.DEFAULT_FONT_COLOR },
	fontSize: 70,
	style: Constants.STYLE_VERTICAL_ALIGN_CENTER | Constants.STYLE_ALIGN_CENTER,
	transform: (element) => {
		element.country = element.country ? element.country.toUpperCase() : element.country;
	},
};

const LINK_IMAGE_ELEMENT_DEFAULTS = {
	xOffset: 230,
	yOffset: 640,
	maxWidth: 500,
	maxHeight: 500,
	style: Constants.STYLE_VERTICAL_ALIGN_TOP | Constants.STYLE_ALIGN_LEFT,
	shadow: null,
};

const IMAGE_ELEMENT_DEFAULTS = {
	xOffset: 478,
	yOffset: 0,
	maxWidth: 2048,
	maxHeight: 1600,
	style: Constants.STYLE_VERTICAL_ALIGN_CENTER | Constants.STYLE_ALIGN_CENTER,
	shadow: {
		radius: 24,
		opacity: 0.8,
	},
};


function ArtistSlidePageContent({ pathname }) {
	const auth = useAuth();
	const dialogs = useDialogs();
	const notifications = useNotifications();

	const [data, setData] = useState(null);
	const [progress, setProgress] = useState(0);
	const [template, setTemplate] = useState(null);
	const [showLoader, setShowLoader] = useState(false);
	const [contentType, setContentType] = useState(null);
	const [nameTemplate, setNameTemplate] = useState(NAME_TEMPLATE);
	const [imageElement, setImageElement] = useState({ ...IMAGE_ELEMENT_DEFAULTS });
	const [linkImageElement, setLinkImageElement] = useState({ ...LINK_IMAGE_ELEMENT_DEFAULTS });
	const [artistTextElement, setArtistTextElement] = useState({ ...ARTIST_TEXT_ELEMENT_DEFAULTS });
	const [countryTextElement, setCountryTextElement] = useState({ ...COUNTRY_TEXT_ELEMENT_DEFAULTS });

	const fileService = new FileService(auth);
	const service = new MagazinePageService(auth);


	const onImageUpload = (data) => {
		setTemplate(data.content);
		setContentType(data.contentType)
	};

	const onDataUpload = (data) => {
		setData(data);
	};

	const onArtistTextElementUpdate = (element) => {
		setArtistTextElement({
			xOffset: element.xOffset,
			yOffset: element.yOffset,
			font: element.font,
			color: element.color,
			fontSize: element.fontSize,
			style: element.style
		});
	};

	const onCountryTextElementUpdate = (element) => {
		setCountryTextElement({
			xOffset: element.xOffset,
			yOffset: element.yOffset,
			font: element.font,
			color: element.color,
			fontSize: element.fontSize,
			style: element.style
		});
	};

	const onLinkImageElementUpdate = (element) => {
		setLinkImageElement({
			xOffset: element.xOffset,
			yOffset: element.yOffset,
			maxWidth: element.maxWidth,
			maxHeight: element.maxHeight,
			style: element.style,
			shadow: element.shadow !== null ? { ...element.shadow, angle: Constants.DEFAULT_SHADOW_ANGLE } : null,
		});
	};

	const onImageElementUpdate = (element) => {
		setImageElement({
			xOffset: element.xOffset,
			yOffset: element.yOffset,
			maxWidth: element.maxWidth,
			maxHeight: element.maxHeight,
			style: element.style,
			shadow: element.shadow !== null ? { ...element.shadow, angle: Constants.DEFAULT_SHADOW_ANGLE } : null,
		});
	};

	const handleSubmit = async () => {
		try {
			setShowLoader(true);
			let operationsCompleted = 0;

			await uploadMissingFiles(fileService, [...data, { content: template }]);

			const payloads = (await buildPayloadChunks(data))
				.map(p => service.postMany(p)
					.finally(() => increaseAndSet()));

			increaseAndSet();

			const results = await Promise
				.all(payloads);

			handleZipDownload(results, resolveZipName(nameTemplate));

			notifications.show('Slides generated successfully.', Constants.NOTIFICATION_SUCCESS);

			function increaseAndSet() {
				setProgress(Math.min(100, Math.round(100 * (operationsCompleted++ / payloads.length))));
			}
		} catch (e) {
			notifications.show('Could not generate slides. ' + e, Constants.NOTIFICATION_ERROR);
		} finally {
			setShowLoader(false);
		}
	};

	const handleUpload = async () => {
		const projectShortCode = await dialogs.prompt('Please specify the project short-code', {
			okText: 'Upload',
			cancelText: 'Cancel',
		});

		if (!projectShortCode) {
			return;
		}

		try {
			setShowLoader(true);
			let operationsCompleted = 0;

			const payloads = (await buildPayloadChunks(data))
				.map(c => { return { ...c, urlPrefix: projectShortCode } })
				.map(p => service.uploadMany(p)
					.finally(() => increaseAndSet()));

			increaseAndSet();

			const results = (await Promise
				.all(payloads))
				.flatMap(r => r);

			handleCsvDownload(results, resolveCsvName(nameTemplate));

			setShowLoader(false);

			notifications.show('Video slides uploaded successfully.', Constants.NOTIFICATION_SUCCESS);

			function increaseAndSet() {
				setProgress(Math.min(100, Math.round(100 * (operationsCompleted++ / payloads.length))));
			}
		} catch (e) {
			notifications.show('Could not upload video slides. ' + e, Constants.NOTIFICATION_ERROR);
		} finally {
			setShowLoader(false);
		}
	};

	const buildPayloadChunks = async (data) => {
		const digests = await Promise.all(data
			.map(async i => {
				return {
					id: i.id,
					artist: i.artist,
					country: i.country,
					website: i.website,
					filename: await digest(i.content),
				}
			}));

		return Promise.all(dataToChunks(digests)
			.map(async c => {
				return {
					template: {
						content: template,
						contentType: contentType,
					},
					images: await Promise.all(c
						.filter(n => !!n)
						.map(n => {
							n.nameTemplate = n.artist;
							[ARTIST_TEXT_ELEMENT_DEFAULTS, COUNTRY_TEXT_ELEMENT_DEFAULTS]
								.filter(e => !!e.transform)
								.forEach(e => e.transform(n));
							return n;
						})
						.map(async n => {
							const artist = {
								class: Constants.ELEMENT_TEXT,
								text: n.artist,
								xOffset: artistTextElement.xOffset,
								yOffset: artistTextElement.yOffset,
								font: artistTextElement.font,
								color: hexToDecimal(artistTextElement.color.hex),
								fontSize: artistTextElement.fontSize,
								style: artistTextElement.style
							};

							const country = {
								class: Constants.ELEMENT_TEXT,
								text: n.country,
								xOffset: countryTextElement.xOffset,
								yOffset: countryTextElement.yOffset,
								font: countryTextElement.font,
								color: hexToDecimal(countryTextElement.color.hex),
								fontSize: countryTextElement.fontSize,
								style: countryTextElement.style
							};

							const link = (n.website && n.website.trim() !== '') ? {
								class: Constants.ELEMENT_IMAGE,
								xOffset: linkImageElement.xOffset,
								yOffset: linkImageElement.yOffset,
								maxWidth: linkImageElement.maxWidth,
								maxHeight: linkImageElement.maxHeight,
								style: linkImageElement.style,
								image: {
									content: await qrcode(n.website, linkImageElement.maxWidth, linkImageElement.maxWidth),
									contentType: 'image/png',
								},
								shadow: (linkImageElement.shadow ? {
									class: Constants.ELEMENT_SHADOW,
									...linkImageElement.shadow,
								} : null),
							} : null;

							const image = {
								class: Constants.ELEMENT_IMAGE,
								xOffset: imageElement.xOffset,
								yOffset: imageElement.yOffset,
								maxWidth: imageElement.maxWidth,
								maxHeight: imageElement.maxHeight,
								style: imageElement.style,
								image: {
									filename: n.filename,
								},
								shadow: (imageElement.shadow ? {
									class: Constants.ELEMENT_SHADOW,
									...imageElement.shadow,
								} : null),
							};

							return {
								artistName: n.nameTemplate,
								filename: nameTemplate
									.replace("{name}", n.nameTemplate)
									.replace("{code}", generateShortCode()),
								elements: [artist, country, image, link]
									.filter(e => !!e)
							}
						}))
				};
			}));
	};

	return (
		<>
			<Divider sx={{ m: 2 }} />
			<Backdrop sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })} open={showLoader} >
				<LinearProgressWithLabel value={progress} />
			</Backdrop>
			<Grid container spacing={4} padding={2} columns={{ xs: 2, sm: 6, md: 6, xl: 12 }}>
				<Grid size={6}>
					<FilePicker onUploadImage={onImageUpload} width={565} height={437} />
				</Grid>
				<Grid size={6}>
					<LoadingMetadataTable onUpload={onDataUpload} columns={COLUMNS} />
				</Grid>

				<Grid size={12}>
					<Accordion>
						<AccordionSummary
							expandIcon={<ExpandMoreIcon />}
							aria-controls="slide-filename-content"
							id="slide-filename-header"
						>
							Slide filename template
						</AccordionSummary>
						<AccordionDetails>
							<TextField value={nameTemplate} label={'Name Template'} onChange={(v) => setNameTemplate(v.target.value)} required fullWidth />
						</AccordionDetails>
					</Accordion>
					<Accordion>
						<AccordionSummary
							expandIcon={<ExpandMoreIcon />}
							aria-controls="artist-style-content"
							id="artist-header"
						>
							Artist name style
        					</AccordionSummary>
						<AccordionDetails>
							<TextMetadataElement
								xOffset={ARTIST_TEXT_ELEMENT_DEFAULTS.xOffset}
								yOffset={ARTIST_TEXT_ELEMENT_DEFAULTS.yOffset}
								font={ARTIST_TEXT_ELEMENT_DEFAULTS.font}
								fontSize={ARTIST_TEXT_ELEMENT_DEFAULTS.fontSize}
								color={ARTIST_TEXT_ELEMENT_DEFAULTS.color}
								style={ARTIST_TEXT_ELEMENT_DEFAULTS.style}
								onUpdate={onArtistTextElementUpdate}
							/>
						</AccordionDetails>
					</Accordion>
					<Accordion>
						<AccordionSummary
							expandIcon={<ExpandMoreIcon />}
							aria-controls="country-style-content"
							id="country-header"
						>
							Country style
        					</AccordionSummary>
						<AccordionDetails>
							<TextMetadataElement
								xOffset={COUNTRY_TEXT_ELEMENT_DEFAULTS.xOffset}
								yOffset={COUNTRY_TEXT_ELEMENT_DEFAULTS.yOffset}
								font={COUNTRY_TEXT_ELEMENT_DEFAULTS.font}
								fontSize={COUNTRY_TEXT_ELEMENT_DEFAULTS.fontSize}
								color={COUNTRY_TEXT_ELEMENT_DEFAULTS.color}
								style={COUNTRY_TEXT_ELEMENT_DEFAULTS.style}
								onUpdate={onCountryTextElementUpdate}
							/>
						</AccordionDetails>
					</Accordion>
					<Accordion>
						<AccordionSummary
							expandIcon={<ExpandMoreIcon />}
							aria-controls="url-style-content"
							id="url-header"
						>
							QR code style
        				</AccordionSummary>
						<AccordionDetails>
							<ImageMetadataElement
								xOffset={LINK_IMAGE_ELEMENT_DEFAULTS.xOffset}
								yOffset={LINK_IMAGE_ELEMENT_DEFAULTS.yOffset}
								maxWidth={LINK_IMAGE_ELEMENT_DEFAULTS.maxWidth}
								maxHeight={LINK_IMAGE_ELEMENT_DEFAULTS.maxHeight}
								style={LINK_IMAGE_ELEMENT_DEFAULTS.style}
								shadow={LINK_IMAGE_ELEMENT_DEFAULTS.shadow}
								onUpdate={onLinkImageElementUpdate}
							/>
						</AccordionDetails>
					</Accordion>
					<Accordion>
						<AccordionSummary
							expandIcon={<ExpandMoreIcon />}
							aria-controls="image-style-content"
							id="image-header"
						>
							Image style
        				</AccordionSummary>
						<AccordionDetails>
							<ImageMetadataElement
								xOffset={IMAGE_ELEMENT_DEFAULTS.xOffset}
								yOffset={IMAGE_ELEMENT_DEFAULTS.yOffset}
								maxWidth={IMAGE_ELEMENT_DEFAULTS.maxWidth}
								maxHeight={IMAGE_ELEMENT_DEFAULTS.maxHeight}
								style={IMAGE_ELEMENT_DEFAULTS.style}
								shadow={IMAGE_ELEMENT_DEFAULTS.shadow}
								onUpdate={onImageElementUpdate}
							/>
						</AccordionDetails>
					</Accordion>
				</Grid>

				<Grid size={6}>
					<Button type={'submit'} color={'primary'} disabled={!template || !data} onClick={handleSubmit}>
						Download
					</Button>
				</Grid>
				<Grid size={6}>
					<LoadingButton variant="contained" disabled={!template || !data} onClick={handleUpload}>
						Upload
					</LoadingButton>
				</Grid>
			</Grid>
		</>
	);
}

export default ArtistSlidePageContent;